import React from 'react'
import {Box} from '@mui/material'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined'
import EventIcon from '@mui/icons-material/Event'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import MedicationOutlinedIcon from '@mui/icons-material/MedicationOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import InfoIcon from '@mui/icons-material/Info'
import LinkIcon from '@mui/icons-material/Link'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined'
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined'
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined'
import {PATHS} from 'src/constants/constants'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import ListItemRow from 'src/pages-new/homepage/ListItemRow'
import {PerformanceItem, DocumentItem, SecondaryTherapyItem, AppointmentItem, InfoMaterialItem} from 'src/pages-new/homepage/types'
import {formatItemDate, getPerformanceStatusIcon, formatLinkSubtitle} from 'src/pages-new/homepage/utils'
import {getStateLabel as getEchoStateLabel} from 'src/pages-new/echocardiographicRequests/echocardiographicRequestsColumns'
import {getStatusLabel as getSampleStatusLabel} from 'src/pages-new/biologicalSamples/biologicalSamplesColumns'
import {getStatusLabel as getExportStatusLabel} from 'src/pages-new/exports/exportsColumns'
import {QUESTIONNAIRE_STATE_LABELS} from 'src/pages-new/questionnaires/questionnairesColumns'

// ---- Metadati statici per categoria ----

export type SupportedCt = 'performances' | 'documents' | 'therapies' | 'appointments' | 'activities' | 'infoMaterials'
  | 'patients' | 'therapyDiary' | 'echocardiographicRequests' | 'biologicalSamples' | 'export' | 'materialRequests' | 'survey'
  | 'myDocuments'

export const SUPPORTED_CTS: SupportedCt[] = [
  'performances', 'documents', 'therapies', 'appointments', 'activities', 'infoMaterials',
  'patients', 'therapyDiary', 'echocardiographicRequests', 'biologicalSamples', 'export', 'materialRequests', 'survey',
  'myDocuments',
]

interface CardMeta {
  defaultTitle: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Icon: React.ComponentType<any>
  path: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emptyIcon: React.ReactElement
  emptyTitle: string
  emptyDescription: string
}

export const CARD_META: Record<SupportedCt, CardMeta> = {
  performances: {
    defaultTitle: 'Prestazioni',
    Icon: CalendarMonthOutlinedIcon,
    path: PATHS.PERFORMANCES,
    emptyIcon: <CalendarMonthOutlinedIcon sx={{fontSize: 48, color: NEUTRAL_SCALE[300]}} />,
    emptyTitle: 'Nessuna prestazione in programma',
    emptyDescription: 'Non ci sono prestazioni in programma',
  },
  documents: {
    defaultTitle: 'Ultimi documenti',
    Icon: DescriptionOutlinedIcon,
    path: PATHS.DOCUMENTS,
    emptyIcon: <DescriptionOutlinedIcon sx={{fontSize: 48, color: NEUTRAL_SCALE[300]}} />,
    emptyTitle: 'Nessun documento caricato',
    emptyDescription: 'Non è stato caricato ancora nessun documento',
  },
  therapies: {
    defaultTitle: 'Altre terapie',
    Icon: MedicalServicesOutlinedIcon,
    path: PATHS.THERAPIES,
    emptyIcon: <MedicationOutlinedIcon sx={{fontSize: 48, color: NEUTRAL_SCALE[300]}} />,
    emptyTitle: 'Nessuna terapia accessoria attiva',
    emptyDescription: 'Non ci sono altre terapie attive',
  },
  appointments: {
    defaultTitle: 'Appuntamenti',
    Icon: EventIcon,
    path: PATHS.AGENDA,
    emptyIcon: <EventIcon sx={{fontSize: 48, color: NEUTRAL_SCALE[300]}} />,
    emptyTitle: 'Nessun appuntamento programmato',
    emptyDescription: 'Non ci sono appuntamenti ancora programmati',
  },
  activities: {
    defaultTitle: 'Attività',
    Icon: AssignmentOutlinedIcon,
    path: PATHS.PATIENT_ACTIVITIES,
    emptyIcon: <AssignmentOutlinedIcon sx={{fontSize: 48, color: NEUTRAL_SCALE[300]}} />,
    emptyTitle: 'Nessuna attività programmata',
    emptyDescription: 'Non ci sono attività ancora programmate',
  },
  infoMaterials: {
    defaultTitle: 'Info e materiali',
    Icon: InfoOutlinedIcon,
    path: PATHS.INFO_MATERIALS,
    emptyIcon: <InfoOutlinedIcon sx={{fontSize: 48, color: NEUTRAL_SCALE[300]}} />,
    emptyTitle: 'Nessun materiale disponibile',
    emptyDescription: 'Non ci sono materiali o link disponibili',
  },
  myDocuments: {
    defaultTitle: 'I miei documenti',
    Icon: FolderSharedOutlinedIcon,
    path: PATHS.MY_DOCUMENTS,
    emptyIcon: <FolderSharedOutlinedIcon sx={{fontSize: 48, color: NEUTRAL_SCALE[300]}} />,
    emptyTitle: 'Nessun documento disponibile',
    emptyDescription: 'Non ci sono documenti personali o condivisi',
  },
  patients: {
    defaultTitle: 'Pazienti',
    Icon: PeopleOutlineIcon,
    path: PATHS.PATIENTS,
    emptyIcon: <PeopleOutlineIcon sx={{fontSize: 48, color: NEUTRAL_SCALE[300]}} />,
    emptyTitle: 'Nessun paziente',
    emptyDescription: 'Non ci sono ancora pazienti',
  },
  therapyDiary: {
    defaultTitle: 'Diario',
    Icon: MenuBookOutlinedIcon,
    path: PATHS.DIARY,
    emptyIcon: <MenuBookOutlinedIcon sx={{fontSize: 48, color: NEUTRAL_SCALE[300]}} />,
    emptyTitle: 'Nessuna assunzione registrata',
    emptyDescription: 'Non ci sono ancora assunzioni nel diario',
  },
  echocardiographicRequests: {
    defaultTitle: 'Richieste ECO',
    Icon: MonitorHeartOutlinedIcon,
    path: PATHS.ECHOCARDIOGRAPHIC_REQUESTS,
    emptyIcon: <MonitorHeartOutlinedIcon sx={{fontSize: 48, color: NEUTRAL_SCALE[300]}} />,
    emptyTitle: 'Nessuna richiesta',
    emptyDescription: 'Non ci sono ancora richieste di esami',
  },
  biologicalSamples: {
    defaultTitle: 'Campioni biologici',
    Icon: ScienceOutlinedIcon,
    path: PATHS.TEST_DBS,
    emptyIcon: <ScienceOutlinedIcon sx={{fontSize: 48, color: NEUTRAL_SCALE[300]}} />,
    emptyTitle: 'Nessun campione',
    emptyDescription: 'Non ci sono ancora campioni biologici',
  },
  export: {
    defaultTitle: 'Export',
    Icon: FileDownloadOutlinedIcon,
    path: PATHS.EXPORT,
    emptyIcon: <FileDownloadOutlinedIcon sx={{fontSize: 48, color: NEUTRAL_SCALE[300]}} />,
    emptyTitle: 'Nessun export',
    emptyDescription: 'Non è stato ancora generato nessun export',
  },
  materialRequests: {
    defaultTitle: 'Richieste materiali',
    Icon: Inventory2OutlinedIcon,
    path: PATHS.MATERIAL_REQUESTS,
    emptyIcon: <Inventory2OutlinedIcon sx={{fontSize: 48, color: NEUTRAL_SCALE[300]}} />,
    emptyTitle: 'Nessuna richiesta',
    emptyDescription: 'Non ci sono ancora richieste di materiali',
  },
  survey: {
    defaultTitle: 'Questionari',
    Icon: QuizOutlinedIcon,
    path: PATHS.QUESTIONNAIRE,
    emptyIcon: <QuizOutlinedIcon sx={{fontSize: 48, color: NEUTRAL_SCALE[300]}} />,
    emptyTitle: 'Nessun questionario',
    emptyDescription: 'Non ci sono questionari disponibili',
  },
}

// ---- CategoryRows: renderer righe condiviso desktop + modale ----

interface CategoryRowsProps {
  ct: SupportedCt
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: any[]
  /** 'desktop' abilita onClick dettaglio; 'modal' abilita solo azioni di primo livello (download) */
  variant: 'desktop' | 'modal'
  onDownloadDocument?: (doc: DocumentItem) => void
  onSelectPerformance?: (p: PerformanceItem) => void
  onSelectTherapy?: (t: SecondaryTherapyItem) => void
  onDownloadMaterial?: (item: InfoMaterialItem) => void
  onOpenLink?: (item: InfoMaterialItem) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDownloadExport?: (exp: any) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDownloadEchoReport?: (request: any) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDownloadAssumption?: (assumption: any) => void
}

export function CategoryRows({
  ct,
  items,
  variant,
  onDownloadDocument,
  onSelectPerformance,
  onSelectTherapy,
  onDownloadMaterial,
  onOpenLink,
  onDownloadExport,
  onDownloadEchoReport,
  onDownloadAssumption,
}: CategoryRowsProps) {
  return (
    <Box sx={{display: 'flex', flexDirection: 'column'}}>
      {ct === 'performances' && (items as PerformanceItem[]).map((p, idx) => (
        <ListItemRow
          key={idx}
          icon={<CalendarMonthOutlinedIcon sx={{fontSize: 22}} />}
          title={p.title ?? '—'}
          subtitle={formatItemDate(p.date)}
          status={p.state}
          statusIcon={getPerformanceStatusIcon(p.state)}
          onClick={variant === 'desktop' && onSelectPerformance ? () => onSelectPerformance(p) : undefined}
        />
      ))}

      {ct === 'documents' && (items as DocumentItem[]).map((d, idx) => (
        <ListItemRow
          key={idx}
          icon={<DescriptionOutlinedIcon sx={{fontSize: 22}} />}
          title={d.name ?? d.fileName ?? '—'}
          subtitle={formatItemDate(d.createdAt)}
          onDownload={onDownloadDocument ? () => onDownloadDocument(d) : undefined}
        />
      ))}

      {ct === 'therapies' && (items as SecondaryTherapyItem[]).map((t, idx) => (
        <ListItemRow
          key={idx}
          icon={<MedicalServicesOutlinedIcon sx={{fontSize: 22}} />}
          title={t.drug?.name ?? '—'}
          subtitle={formatItemDate(t.startDate)}
          onClick={variant === 'desktop' && onSelectTherapy ? () => onSelectTherapy(t) : undefined}
        />
      ))}

      {ct === 'appointments' && (items as AppointmentItem[]).map((a, idx) => (
        <ListItemRow
          key={idx}
          icon={<EventIcon sx={{fontSize: 22}} />}
          title={a.title ?? '—'}
          subtitle={formatItemDate(a.startDate)}
        />
      ))}

      {ct === 'activities' && items.map((a, idx) => (
        <ListItemRow
          key={idx}
          icon={<AssignmentOutlinedIcon sx={{fontSize: 22}} />}
          title={a.beneficiary
            ? `${a.beneficiary.firstName} ${a.beneficiary.lastName} - ${a.title ?? ''}`
            : (a.title ?? '—')}
          subtitle={formatItemDate(a.startDate)}
        />
      ))}

      {ct === 'infoMaterials' && (items as InfoMaterialItem[]).map((item, idx) => (
        <ListItemRow
          key={idx}
          icon={item.kind === 'material'
            ? <InfoIcon sx={{fontSize: 22}} />
            : <LinkIcon sx={{fontSize: 22}} />}
          title={item.title}
          subtitle={item.kind === 'link' ? formatLinkSubtitle(item.url) : formatItemDate(item.date)}
          onDownload={item.kind === 'material' && onDownloadMaterial ? () => onDownloadMaterial(item) : undefined}
          onClick={item.kind === 'link' && onOpenLink ? () => onOpenLink(item) : undefined}
        />
      ))}

      {ct === 'myDocuments' && (items as DocumentItem[]).map((d, idx) => (
        <ListItemRow
          key={idx}
          icon={<FolderSharedOutlinedIcon sx={{fontSize: 22}} />}
          title={d.name ?? d.fileName ?? '—'}
          subtitle={formatItemDate(d.createdAt)}
          onDownload={onDownloadDocument && d.fileId ? () => onDownloadDocument(d) : undefined}
        />
      ))}

      {ct === 'patients' && items.map((p, idx) => (
        <ListItemRow
          key={idx}
          icon={<PeopleOutlineIcon sx={{fontSize: 22}} />}
          title={`${p.lastName ?? ''} ${p.firstName ?? ''}`.trim() || '—'}
          subtitle={p.email ?? ''}
        />
      ))}

      {ct === 'therapyDiary' && items.map((a, idx) => (
        <ListItemRow
          key={idx}
          icon={<MenuBookOutlinedIcon sx={{fontSize: 22}} />}
          title={(typeof a.drug === 'string' ? a.drug : a.drug?.name) ?? '—'}
          subtitle={formatItemDate(a.date)}
          status={a.state}
          onDownload={onDownloadAssumption && a._id ? () => onDownloadAssumption(a) : undefined}
          downloadLabel='Scarica scheda infusionale'
        />
      ))}

      {ct === 'echocardiographicRequests' && items.map((r, idx) => (
        <ListItemRow
          key={idx}
          icon={<MonitorHeartOutlinedIcon sx={{fontSize: 22}} />}
          title={r.title ?? '—'}
          subtitle={formatItemDate(r.createdAt)}
          status={r.state ? getEchoStateLabel(r.state) : undefined}
          onDownload={onDownloadEchoReport && r.patientId && r.examId ? () => onDownloadEchoReport(r) : undefined}
          downloadLabel='Scarica referto'
        />
      ))}

      {ct === 'biologicalSamples' && items.map((s, idx) => (
        <ListItemRow
          key={idx}
          icon={<ScienceOutlinedIcon sx={{fontSize: 22}} />}
          title={`${s.patientLastName ?? ''} ${s.patientFirstName ?? ''}`.trim() || '—'}
          subtitle={formatItemDate(s.date)}
          status={s.status ? getSampleStatusLabel(s.status) : undefined}
        />
      ))}

      {ct === 'export' && items.map((e, idx) => (
        <ListItemRow
          key={idx}
          icon={<DescriptionOutlinedIcon sx={{fontSize: 22}} />}
          title={e.filename ?? '—'}
          subtitle={formatItemDate(e.createdAt)}
          status={e.status ? getExportStatusLabel(e.status) : undefined}
          onDownload={onDownloadExport && e.status === 'created' && e.fileServiceName ? () => onDownloadExport(e) : undefined}
          downloadLabel='Scarica export'
        />
      ))}

      {ct === 'materialRequests' && items.map((m, idx) => (
        <ListItemRow
          key={idx}
          icon={<Inventory2OutlinedIcon sx={{fontSize: 22}} />}
          title={m.materialName ?? '—'}
          subtitle={formatItemDate(m.requestDate)}
          status={m.requestedQuantity !== undefined && m.requestedQuantity !== null ? `Qtà ${m.requestedQuantity}` : undefined}
        />
      ))}

      {ct === 'survey' && items.map((q, idx) => (
        <ListItemRow
          key={idx}
          icon={<QuizOutlinedIcon sx={{fontSize: 22}} />}
          title={q.name ?? '—'}
          subtitle={formatItemDate(q.requestDate ?? q.createdAt)}
          status={q.translatedState ?? (q.state ? QUESTIONNAIRE_STATE_LABELS[q.state] ?? q.state : undefined)}
        />
      ))}
    </Box>
  )
}
